import type {
  SmartReport,
  SmartReportPage,
  SmartReportPageElement,
  SmartReportVersion,
} from '@/types/GraphqlSchema';
import { FullPageLayout } from '../Layouts/FullPage';
import type { LayoutDefinition } from '../Layouts/types';

export const ACTION_TYPES = {
  SET_REPORT: 'SET_REPORT',
  SET_SELECTED_PAGE: 'SET_SELECTED_PAGE',
  SET_SELECTED_LAYOUT: 'SET_SELECTED_LAYOUT',
  ADD_PAGE: 'ADD_PAGE',
  REMOVE_PAGE: 'REMOVE_PAGE',
  UPDATE_PAGE_ELEMENT: 'UPDATE_PAGE_ELEMENT',
  REMOVE_PAGE_ELEMENT: 'REMOVE_PAGE_ELEMENT',
  APPLY_LAYOUT: 'APPLY_LAYOUT',
} as const;

export type EditSmartReportState = {
  report: SmartReport | null;
  reportVersion: SmartReportVersion | null;
  selectedPage: SmartReportPage | null;
  selectedLayout: LayoutDefinition | null;
};

type ActionType = typeof ACTION_TYPES;

type SetReportAction = {
  type: ActionType['SET_REPORT'];
  payload: { report: SmartReport | null };
};

type SetSelectedPageAction = {
  type: ActionType['SET_SELECTED_PAGE'];
  payload: { page: SmartReportPage | null };
};

type SetSelectedLayoutAction = {
  type: ActionType['SET_SELECTED_LAYOUT'];
  payload: { layout: LayoutDefinition | null };
};

type AddPageAction = {
  type: ActionType['ADD_PAGE'];
  payload: { layout: LayoutDefinition | null };
};

type RemovePageAction = {
  type: ActionType['REMOVE_PAGE'];
  payload: { pageNumber: number };
};

type UpdatePageElementAction = {
  type: ActionType['UPDATE_PAGE_ELEMENT'];
  payload: {
    componentId: string;
    slotId: string;
    data: unknown;
  };
};

type RemovePageElementAction = {
  type: ActionType['REMOVE_PAGE_ELEMENT'];
  payload: { slotId: string };
};

type ApplyLayoutAction = {
  type: ActionType['APPLY_LAYOUT'];
  payload: { layout: LayoutDefinition };
};

export type EditSmartReportAction =
  | SetReportAction
  | SetSelectedPageAction
  | SetSelectedLayoutAction
  | AddPageAction
  | RemovePageAction
  | UpdatePageElementAction
  | RemovePageElementAction
  | ApplyLayoutAction;

export const INITIAL_STATE: EditSmartReportState = {
  report: null,
  reportVersion: null,
  selectedPage: null,
  selectedLayout: null,
};

const getLatestReportVersion = (
  versions: SmartReportVersion[] | null | undefined,
): SmartReportVersion | null => {
  if (!Array.isArray(versions) || versions.length === 0) return null;

  const firstVersion = versions[0];
  if (!firstVersion) return null;

  let latest = firstVersion;
  for (const current of versions) {
    if (current.number > latest.number) {
      latest = current;
    }
  }
  return latest;
};

const ensureMinimumPages = (pages: SmartReportPage[] | null | undefined): SmartReportPage[] => {
  if (Array.isArray(pages) && pages.length > 0) return pages;

  return [
    {
      __typename: 'SmartReportPage',
      number: 1,
      layout: FullPageLayout.id,
      pageElements: [],
      description: null,
      thumbnailUrl: null,
    },
  ];
};

const reindexPages = (pages: SmartReportPage[]): SmartReportPage[] => {
  return pages.map((page, index) => ({
    ...page,
    number: index + 1,
  }));
};

export const editSmartReportReducer = (
  state: EditSmartReportState,
  action: EditSmartReportAction,
): EditSmartReportState => {
  switch (action.type) {
    case ACTION_TYPES.SET_REPORT: {
      const { report } = action.payload;

      if (!report) {
        return INITIAL_STATE;
      }

      const latestVersion = getLatestReportVersion(report.smartReportVersions);

      if (!latestVersion) {
        return { ...state, report, reportVersion: null, selectedPage: null };
      }

      const pages = ensureMinimumPages(latestVersion.pages);
      const reportVersion: SmartReportVersion = { ...latestVersion, pages };
      const selectedPage = state.selectedPage || pages[0] || null;

      return {
        ...state,
        report,
        reportVersion,
        selectedPage,
      };
    }

    case ACTION_TYPES.SET_SELECTED_PAGE: {
      return {
        ...state,
        selectedPage: action.payload.page,
      };
    }
    case ACTION_TYPES.SET_SELECTED_LAYOUT: {
      return {
        ...state,
        selectedLayout: action.payload.layout,
      };
    }

    case ACTION_TYPES.ADD_PAGE: {
      const { layout } = action.payload;
      const currentPages = state.reportVersion?.pages || [];
      const newPage: SmartReportPage = {
        __typename: 'SmartReportPage',
        number: currentPages.length + 1,
        layout: layout?.id || FullPageLayout.id,
        pageElements: [],
        description: null,
        thumbnailUrl: null,
      };

      const updatedPages = [...currentPages, newPage];

      if (!state.reportVersion) return state;

      const updatedReportVersion: SmartReportVersion = {
        ...state.reportVersion,
        pages: updatedPages,
      };

      return {
        ...state,
        reportVersion: updatedReportVersion,
        selectedPage: newPage,
      };
    }

    case ACTION_TYPES.REMOVE_PAGE: {
      const { pageNumber } = action.payload;
      const currentPages = state.reportVersion?.pages || [];

      if (currentPages.length <= 1 || !state.reportVersion) {
        return state;
      }

      const filteredPages = currentPages.filter(page => page.number !== pageNumber);
      const reindexedPages = reindexPages(filteredPages);

      const updatedReportVersion: SmartReportVersion = {
        ...state.reportVersion,
        pages: reindexedPages,
      };

      // Update selected page if necessary
      let newSelectedPage = state.selectedPage;
      if (state.selectedPage?.number === pageNumber) {
        const targetIndex = Math.max(0, pageNumber - 2);
        newSelectedPage = reindexedPages[targetIndex] || null;
      }

      return {
        ...state,
        reportVersion: updatedReportVersion,
        selectedPage: newSelectedPage,
      };
    }

    case ACTION_TYPES.UPDATE_PAGE_ELEMENT: {
      const { componentId, slotId, data } = action.payload;
      const { selectedPage } = state;

      if (!selectedPage || !state.reportVersion) return state;

      const elements = [...(selectedPage?.pageElements || [])];
      const existingIndex = elements.findIndex(el => el.slotId === slotId);

      const newElement: SmartReportPageElement = {
        __typename: 'SmartReportPageElement',
        componentId,
        slotId,
        data: data || null,
        payload: null,
        style: {},
      };

      if (existingIndex >= 0) {
        elements[existingIndex] = newElement;
      } else {
        elements.push(newElement);
      }

      const updatedSelectedPage: SmartReportPage = {
        ...selectedPage,
        pageElements: elements,
      };

      const updatedPages = state.reportVersion.pages.map(page =>
        page.number === selectedPage.number ? updatedSelectedPage : page,
      );

      const updatedReportVersion: SmartReportVersion = {
        ...state.reportVersion,
        pages: updatedPages,
      };

      return {
        ...state,
        selectedPage: updatedSelectedPage,
        reportVersion: updatedReportVersion,
      };
    }

    case ACTION_TYPES.REMOVE_PAGE_ELEMENT: {
      const { slotId } = action.payload;
      const { selectedPage } = state;

      if (!selectedPage || !state.reportVersion) return state;

      const filteredElements = selectedPage.pageElements.filter(
        element => element.slotId !== slotId,
      );

      const updatedSelectedPage: SmartReportPage = {
        ...selectedPage,
        pageElements: filteredElements,
      };

      const updatedPages = state.reportVersion.pages.map(page =>
        page.number === selectedPage.number ? updatedSelectedPage : page,
      );

      const updatedReportVersion: SmartReportVersion = {
        ...state.reportVersion,
        pages: updatedPages,
      };

      return {
        ...state,
        selectedPage: updatedSelectedPage,
        reportVersion: updatedReportVersion,
      };
    }

    case ACTION_TYPES.APPLY_LAYOUT: {
      const { layout } = action.payload;
      const { selectedPage } = state;

      if (!selectedPage || !layout || !state.reportVersion) return state;

      const updatedSelectedPage: SmartReportPage = {
        ...selectedPage,
        layout: layout.id,
        pageElements: [],
      };

      const updatedPages = state.reportVersion.pages.map(page =>
        page.number === selectedPage.number ? updatedSelectedPage : page,
      );

      const updatedReportVersion: SmartReportVersion = {
        ...state.reportVersion,
        pages: updatedPages,
      };

      return {
        ...state,
        selectedPage: updatedSelectedPage,
        selectedLayout: layout,
        reportVersion: updatedReportVersion,
      };
    }

    default:
      return state;
  }
};
